import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { assets, dialogs, i18next, view, bowser, linkEditing } from '@yola/ws-sdk';
import { Modal, PanelGroup, Panel, designSystem, reactHookForm } from '@yola/ws-ui';
import UploadImageContainer from 'src/js/modules/common/containers/upload-image-container';
import ReplaceImageButton from 'src/js/modules/common/components/replace-image-button';
import MediaPreview from 'src/js/modules/common/components/media-preview';
import customUI from 'src/js/modules/custom-ui';
import contextMenu from 'src/js/modules/context-menu';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import getDefaultTraits from 'src/js/modules/analytics/segment/helpers/get-default-traits';
import errorCodes from 'src/js/modules/extensions/common/constants/error-codes';
import imageTypes from 'src/js/modules/extensions/common/constants/image-types';
import linkAttributes from 'src/js/modules/extensions/common/constants/link-attributes';
import getStatusOfChangedData from 'src/js/modules/extensions/registry/ws-image/helpers/get-status-of-changed-data';
import getClosestBlockElement from 'src/js/modules/common/helpers/get-closest-block-element';
import focalPoint from 'src/js/modules/focal-point';
import segment from 'src/js/modules/analytics/segment';
import LinkSettingContainer from 'src/js/modules/common/containers/link-setting-container';
import constants from 'src/js/modules/common/constants';
import processLinkSubmit from 'src/js/modules/common/helpers/process-link-submit';
import mediaTypes from '../constants/media-types';
import mediaAttributes from '../constants/media-attributes';
import trackers from '../trackers';
import getLinkConfig from '../helpers/get-link-config';
import buildLinkElement from '../helpers/build-link-element';
import buildPictureElement from '../helpers/build-picture-element';
import getDataFromMediaNode from '../helpers/get-data-from-media-node';
import fieldNames from '../constants/media-settings-field-names';

const {
  ActionButton,
  Heading,
  ControlGroup,
  Stack,
  IconButton,
  Tooltip,
  InputGroupField,
  TextAreaField,
  CheckboxField,
  FieldStatus,
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
} = designSystem;

const { useForm, useWatch } = reactHookForm;

const {
  constants: { common: focalPointConstants, operationTypes },
  helpers: focalPointHelpers,
} = focalPoint;

const {
  getViewOperation,
  setPosition,
  setScale,
  setCurrentImageUrl,
  setOriginImageSize,
  computeAdjustedScale,
} = focalPointHelpers;

const {
  track,
  constants: { events },
} = segment;

const REGULAR_IMAGE_REPLACE_TRIGGER = 'regular-image-replace-trigger';
const PANEL_HEIGHT = 48;
const DIALOG_WIDTH = 336;
const DIALOG_HEIGHT = 595;

const { linkTypes } = linkEditing.constants;

const {
  trackMediaSettingsDialogSubmitted,
  trackMediaSettingsInputClicked,
  trackMediaSettingsOptionClicked,
} = trackers;

const getInitialMediaState = (
  latestValues,
  mediaGalleryItemState,
  initialLinkElement,
  mediaElement
) => {
  if (latestValues) {
    return {
      ...latestValues,
    };
  }

  if (mediaGalleryItemState) {
    return {
      ...mediaGalleryItemState,
    };
  }

  const mediaData = getDataFromMediaNode(mediaElement);
  const linkConfig = getLinkConfig(initialLinkElement, mediaElement);
  const shouldUseTitleForAlt = mediaData?.alt === mediaData?.title;

  return {
    ...mediaData,
    linkConfig,
    shouldUseTitleForAlt,
  };
};

const defaultOperations = [];

const MediaSettingsDialog = ({
  elementId,
  operations: mediaOperations = defaultOperations,
  getSharedData,
  resolveSharedData,
  onSubmit,
  onCancel,
  onOpenDialog,
  onDeleteImage = Function.prototype,
  mediaType = mediaTypes.REGULAR,
  mediaGalleryItemState = null,
  showDeleteButton = false,
}) => {
  const mediaElement = view.accessors.getLiveElement(elementId);
  const initialLinkElement = mediaElement.querySelector('a');
  const initialImageElement = mediaElement.querySelector('img');
  const initialPictureElement = mediaElement.querySelector('picture') || initialImageElement;
  const isGalleryMediaType =
    mediaType === mediaTypes.GALLERY || mediaType === mediaTypes.GALLERY_ITEM;

  const blockElement = getClosestBlockElement(mediaElement);
  const blockElementId = view.accessors.getLiveElementId(blockElement);

  const placeholderType = view.accessors.getElementAttribute(blockElementId, 'category') || null;

  const { latestValues } = getSharedData();
  const initialMediaStateRef = useRef(
    getInitialMediaState(latestValues, mediaGalleryItemState, initialLinkElement, mediaElement)
  );
  const scrollableRef = useRef(null);
  const linkSettingsValidationRef = useRef(null);

  const allowedLinkTypes = [
    linkTypes.NO_ACTION,
    linkTypes.PAGE,
    linkTypes.PHONE,
    linkTypes.MAIL,
    linkTypes.DOWNLOAD,
    linkTypes.ANCHOR,
    linkTypes.EXTERNAL,
  ];

  if (isGalleryMediaType) {
    allowedLinkTypes.splice(1, 0, linkTypes.VIEW_FULL_SIZE_IMAGE);
  }

  const { offsetX, offsetY } = dialogs.helpers.getPositionByElement(
    mediaElement,
    DIALOG_WIDTH,
    DIALOG_HEIGHT
  );

  const [mediaState, setMediaState] = useState(initialMediaStateRef.current);
  const [dialogPosition, setDialogPosition] = useState({
    offsetX,
    offsetY,
  });

  const { control, setValue } = useForm({
    defaultValues: {
      [fieldNames.TITLE]: initialMediaStateRef.current.title,
      [fieldNames.ALT]: initialMediaStateRef.current.alt,
      [fieldNames.DESCRIPTION]: initialMediaStateRef.current.description,
      [fieldNames.USE_TITLE_FOR_ALT]: initialMediaStateRef.current.shouldUseTitleForAlt,
    },
    mode: 'onTouched',
  });

  const [titleValue, shouldUseTitleForAlt] = useWatch({
    control,
    name: [fieldNames.TITLE, fieldNames.USE_TITLE_FOR_ALT],
  });

  const saveSharedData = useCallback(
    (values = {}) => {
      const { initialValues } = getSharedData();

      const sharedData = {
        latestValues: {
          ...mediaState,
          ...dialogPosition,
          ...values,
        },
      };

      if (!initialValues) {
        sharedData.initialValues = {
          ...mediaState,
        };
      }

      resolveSharedData(sharedData);
    },
    [dialogPosition, mediaState, getSharedData, resolveSharedData]
  );

  const onImageReplace = async ({ source: assetSource, isStockPhoto }) => {
    const { isPlaceholder } = mediaState;
    let replacedSharedData = {};

    track(events.MEDIA_SETTINGS_NEW_IMAGE_SELECTED, {
      ...getDefaultTraits(elementId),
      dialogId: dialogTypes.MEDIA_SETTINGS_DIALOG,
      source: isStockPhoto ? focalPointConstants.STOCK_PHOTOS : focalPointConstants.FILE_UPLOAD,
    });

    const imageUrl = assets.helpers.addBaseHref(assetSource);
    const imageSize = await assets.helpers.getImageSize(imageUrl);

    if (isGalleryMediaType) {
      const { width, height } = imageSize;

      setCurrentImageUrl(mediaElement, assetSource);
      setOriginImageSize(mediaElement, imageSize);

      replacedSharedData = {
        src: assetSource,
        originSrc: assetSource,
        originWidth: width,
        originHeight: height,
      };
    } else {
      setCurrentImageUrl(mediaElement, assetSource);

      replacedSharedData = {
        src: assetSource,
      };
    }

    if (isPlaceholder) {
      mediaElement.removeAttribute(mediaAttributes.PLACEHOLDER_ATTRIBUTE);
    }

    // adjusting content scale so small images want be stretching to container width or height
    const contentScale = computeAdjustedScale(mediaElement, {
      originalMediaBounds: imageSize,
      scale: focalPointConstants.DEFAULT_SCALE,
    });
    const contentPosition = focalPointConstants.DEFAULT_POSITION_STRING_VALUE;

    setScale(mediaElement, contentScale);
    setPosition(mediaElement, contentPosition);

    saveSharedData({
      ...replacedSharedData,
      contentScale,
      contentPosition,
      isPlaceholder: false,
    });

    onOpenDialog();
  };

  const handleElementUpdates = useCallback(
    (data) => {
      const { initialValues } = getSharedData();
      const changedData = getStatusOfChangedData(data, initialValues);
      const {
        isClickActionChanged,
        isSrcChanged,
        isTitleChanged,
        isAltChanged,
        isDescriptionChanged,
        isScaleChanged,
        isPositionChanged,
      } = changedData;
      const operations = [...mediaOperations];
      const isGalleryDataChanged = isGalleryMediaType && (isTitleChanged || isDescriptionChanged);

      if (isGalleryMediaType) {
        const { lightboxDisabled } = data;

        if (lightboxDisabled) {
          operations.push([
            view.operations.setElementAttribute,
            [elementId, mediaAttributes.LIGHTBOX_DISABLE_ATTRIBUTE, ''],
          ]);
        } else {
          operations.push([
            view.operations.removeElementAttribute,
            [elementId, mediaAttributes.LIGHTBOX_DISABLE_ATTRIBUTE],
          ]);
        }

        if (isSrcChanged) {
          const { originSrc, originWidth, originHeight } = data;

          operations.push(
            [view.operations.setElementAttribute, [elementId, 'origin-src', originSrc]],
            [view.operations.setElementAttribute, [elementId, 'origin-width', originWidth]],
            [view.operations.setElementAttribute, [elementId, 'origin-height', originHeight]]
          );
        }
      }

      if (isScaleChanged || isPositionChanged) {
        const { contentScale, contentPosition } = data;

        const setScaleOperation = getViewOperation(operationTypes.SCALE, elementId, contentScale);
        const setPositionOperation = getViewOperation(
          operationTypes.POSITION,
          elementId,
          contentPosition
        );

        operations.push(setScaleOperation, setPositionOperation);
      }

      if (isClickActionChanged || isSrcChanged || isAltChanged || isGalleryDataChanged) {
        const { linkType } = data.linkConfig;
        const { src, alt, title, description } = data;
        let galleryPictureAttrs = {};

        if (linkType === linkTypes.VIEW_FULL_SIZE_IMAGE) {
          galleryPictureAttrs = { title, description };
        }

        const pictureElement = buildPictureElement({ src, alt, ...galleryPictureAttrs });
        let newElement = pictureElement;

        if (linkType !== linkTypes.NO_ACTION && linkType !== linkTypes.VIEW_FULL_SIZE_IMAGE) {
          const linkElement = buildLinkElement(data.linkConfig);
          linkElement.classList.add(linkAttributes.CLICK_ACTION_LINK_CLASS);
          linkElement.appendChild(pictureElement);
          newElement = linkElement;
        }

        const target = initialLinkElement || initialPictureElement;
        const targetElementId = view.accessors.getLiveElementId(target);

        operations.push([view.operations.replaceElement, [newElement, targetElementId]]);
      }

      if (initialValues?.isPlaceholder && isSrcChanged) {
        operations.push([
          view.operations.removeElementAttribute,
          [elementId, mediaAttributes.PLACEHOLDER_ATTRIBUTE],
        ]);
      }

      const { linkConfig } = mediaState;

      trackMediaSettingsDialogSubmitted(elementId, {
        ...changedData,
        customPath:
          linkTypes.DOWNLOAD === linkConfig.linkType ? linkConfig.customPath || null : null,
        setDownloadUrlAdjusted:
          linkTypes.DOWNLOAD === linkConfig.linkType ? Boolean(linkConfig.customPath) : null,
      });

      onSubmit([...operations], data);
    },
    [
      elementId,
      getSharedData,
      initialPictureElement,
      initialLinkElement,
      isGalleryMediaType,
      mediaOperations,
      mediaState,
      onSubmit,
    ]
  );

  const handleCancel = useCallback(() => {
    const { initialValues } = getSharedData();
    const initialSrc = initialValues.src;
    const initialPosition = initialValues.contentPosition;
    const initialScale = initialValues.contentScale;

    track(events.MEDIA_SETTINGS_DIALOG_CANCELED, {
      ...getDefaultTraits(elementId),
      dialogId: dialogTypes.MEDIA_SETTINGS_DIALOG,
    });

    setCurrentImageUrl(mediaElement, initialSrc);
    setPosition(mediaElement, initialPosition);
    setScale(mediaElement, initialScale);

    onCancel();
  }, [elementId, getSharedData, mediaElement, onCancel]);

  const handleUpdateMediaState = (optionName, value) =>
    setMediaState((prev) => ({
      ...prev,
      [optionName]: value,
    }));

  const handleUseTitleForAltChange = useCallback(
    (value) => {
      trackMediaSettingsOptionClicked(elementId, value);

      const newAlt = value ? titleValue : '';

      setValue(fieldNames.ALT, newAlt);
      handleUpdateMediaState(fieldNames.ALT, newAlt);
    },
    [elementId, setValue, titleValue]
  );

  const handleCrop = useCallback(() => {
    saveSharedData();

    customUI.operations.show(customUI.customUiTypes.EDIT_INLINE_IMAGE_FOCAL_POINT, {
      elementId,
      onSubmit: ({ operations }) => {
        const contentPosition = mediaElement.getAttribute('content-position');
        const contentScale = mediaElement.getAttribute('content-scale');
        const aspectRatio = mediaElement.getAttribute('aspect-ratio');

        saveSharedData({ contentPosition, contentScale, aspectRatio });

        onOpenDialog({
          operations,
        });
      },
      onCancel: () => onOpenDialog(),
      preserveSharedData: true,
      shouldApplyChangesOnSubmit: false,
    });

    track(events.MEDIA_SETTINGS_CROP_BUTTON_CLICKED, {
      ...getDefaultTraits(elementId),
      dialogId: dialogTypes.MEDIA_SETTINGS_DIALOG,
    });
  }, [elementId, mediaElement, onOpenDialog, saveSharedData]);

  const handleLinkChange = useCallback(
    (linkConfig, linkSettings) => {
      const galleryData = isGalleryMediaType
        ? {
            lightboxDisabled: linkConfig.linkType !== linkTypes.VIEW_FULL_SIZE_IMAGE,
          }
        : {};

      setMediaState((prev) => ({
        ...prev,
        linkConfig,
        ...galleryData,
      }));

      linkSettingsValidationRef.current = linkSettings;
    },
    [isGalleryMediaType]
  );

  const handleSubmitForm = useCallback(async () => {
    const { linkConfig } = mediaState;

    saveSharedData();

    const { trigger, errors } = linkSettingsValidationRef.current;

    await trigger();

    if (Object.keys(errors).length) {
      return;
    }

    processLinkSubmit({
      config: linkConfig,
    })
      .then((finalLinkConfig) => {
        handleElementUpdates({
          ...mediaState,
          linkConfig: finalLinkConfig,
        });
      })
      .catch((code) => {
        if (
          code === errorCodes.UPLOAD_FILE_CANCELED ||
          code === errorCodes.UPSELL_INTERRUPTION_CANCELLED
        ) {
          handleCancel();
        }
      });
  }, [handleCancel, handleElementUpdates, mediaState, saveSharedData]);

  const { src, linkConfig, isPlaceholder } = mediaState;

  const isViewFullSizeImageLinkType = linkConfig.linkType === linkTypes.VIEW_FULL_SIZE_IMAGE;

  const panelClassName = classNames('ws-media-settings-dialog__panel', {
    'ws-media-settings-dialog__panel--mobile': bowser.mobile,
  });
  const replaceImageButtonLabel = isPlaceholder
    ? i18next.t('Add image')
    : i18next.t('Replace image');
  const mediaPreviewUrl = isPlaceholder ? '' : assets.helpers.addBaseHref(src);

  useEffect(() => {
    const sharedData = getSharedData();

    if (!Object.keys(sharedData).length || Object.values(sharedData).some((value) => !value)) {
      saveSharedData();
    }

    linkSettingsValidationRef.current.trigger();

    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  useEffect(() => Tooltip.rebuild(), [mediaState]);

  return (
    <Modal
      dragHandleSelector=".ws-drag-trigger"
      resizable={false}
      width={DIALOG_WIDTH}
      height={DIALOG_HEIGHT}
      fullscreen={bowser.mobile}
      offsetX={dialogPosition.offsetX}
      offsetY={dialogPosition.offsetY}
      onDragEnd={(_, { x, y }) =>
        setDialogPosition({
          offsetX: x,
          offsetY: y,
        })
      }
      handleCancel={handleCancel}
      handleSubmit={handleSubmitForm}
      className="ws-media-settings-dialog"
      floatingModalElementSelector=".ws-context-menu"
    >
      <PanelGroup height="100%">
        <Panel
          height={PANEL_HEIGHT}
          align="middle"
          className="ws-drag-trigger ws-modal__content ws-media-settings-dialog__header"
          theme="gray-100"
          corners="squared"
        >
          <Heading type="heading-6">{i18next.t('Media settings')}</Heading>
        </Panel>
        <Panel
          theme="white"
          corners="squared"
          height="100%"
          className={panelClassName}
          scrollable
          rootRef={scrollableRef}
        >
          <div className="ws-modal__content">
            <div className="ws-modal__block">
              <Heading type="heading-6">{i18next.t('Media')}</Heading>
              <div className="ws-media-settings-dialog__img-wrapper">
                <MediaPreview placeholderType={placeholderType} url={mediaPreviewUrl} />
                {!isPlaceholder && (
                  <div className="ws-media-settings-dialog__crop-btn">
                    <IconButton
                      onClick={handleCrop}
                      glyph="crop"
                      size="extra-large"
                      label={i18next.t('Crop')}
                    />
                  </div>
                )}
                <div className="ws-media-settings-dialog__btn-wrapper">
                  <UploadImageContainer onImageUploadEnd={onImageReplace} preserveSharedData>
                    {(imageContainerProps) => (
                      <ReplaceImageButton
                        id={REGULAR_IMAGE_REPLACE_TRIGGER}
                        label={replaceImageButtonLabel}
                        elementId={elementId}
                        accept={imageTypes.IMAGE_ACCEPT_TYPE}
                        onPhotoStockDialogCancel={() => onOpenDialog()}
                        {...imageContainerProps}
                        onClick={() => {
                          track(events.MEDIA_SETTINGS_REPLACE_MEDIA_BUTTON_CLICKED, {
                            ...getDefaultTraits(elementId),
                            dialogId: dialogTypes.MEDIA_SETTINGS_DIALOG,
                          });

                          // we need to hide context menu when user scroll inside dialog
                          scrollableRef?.current?.addEventListener(
                            'scroll',
                            () => {
                              contextMenu.operations.hideContextMenu();
                            },
                            { once: true }
                          );

                          saveSharedData();
                        }}
                        onWrongFileType={() => {
                          dialogs.operations.show(dialogTypes.FILE_TYPE_ERROR_DIALOG, {
                            onDismiss: () => onOpenDialog(),
                          });
                        }}
                        onTriggerUploadImageClick={() => {
                          track(events.MEDIA_SETTINGS_UPLOAD_FILE_BUTTON_CLICKED, {
                            ...getDefaultTraits(elementId),
                            dialogId: dialogTypes.MEDIA_SETTINGS_DIALOG,
                          });
                        }}
                        onTriggerBrowseStockPhotosClick={() => {
                          track(events.MEDIA_SETTINGS_BROWSE_STOCK_PHOTOS_BUTTON_CLICKED, {
                            ...getDefaultTraits(elementId),
                            dialogId: dialogTypes.MEDIA_SETTINGS_DIALOG,
                          });
                        }}
                      />
                    )}
                  </UploadImageContainer>
                </div>
              </div>
            </div>

            {isViewFullSizeImageLinkType && (
              <div className="ws-modal__block">
                <Stack gap="spacing-xs">
                  <ControlGroup title={i18next.t('Image title')}>
                    <InputGroupField
                      placeholder={i18next.t('Enter title for media')}
                      name={fieldNames.TITLE}
                      control={control}
                      onChange={(value) => {
                        if (shouldUseTitleForAlt) {
                          const trimmedValue = value.trim();
                          setValue(fieldNames.ALT, trimmedValue);
                        }

                        handleUpdateMediaState(fieldNames.TITLE, value);
                      }}
                      onFocus={() => {
                        trackMediaSettingsInputClicked(elementId, 'image-title');
                      }}
                    />
                  </ControlGroup>
                  <CheckboxField
                    name={fieldNames.USE_TITLE_FOR_ALT}
                    control={control}
                    label={i18next.t('Same title for Alt tag')}
                    onChange={(value) => handleUseTitleForAltChange(value)}
                  />
                </Stack>
              </div>
            )}

            {(!isViewFullSizeImageLinkType || !shouldUseTitleForAlt) && (
              <div className="ws-modal__block">
                <ControlGroup title={i18next.t('Alt title')}>
                  <InputGroupField
                    name={fieldNames.ALT}
                    control={control}
                    onChange={(value) => handleUpdateMediaState(fieldNames.ALT, value)}
                    onFocus={() => {
                      trackMediaSettingsInputClicked(elementId, 'alt-title');
                    }}
                  />
                  <FieldStatus
                    text={i18next.t(
                      'Search engines cannot see images but they can read their descriptions. Add image description to boost your search engine visibility.'
                    )}
                  />
                </ControlGroup>
              </div>
            )}

            {isViewFullSizeImageLinkType && (
              <div className="ws-modal__block">
                <ControlGroup title={i18next.t('Description')}>
                  <TextAreaField
                    placeholder={i18next.t('Enter description for media')}
                    name={fieldNames.DESCRIPTION}
                    control={control}
                    onChange={(value) => handleUpdateMediaState(fieldNames.DESCRIPTION, value)}
                    onFocus={() => {
                      trackMediaSettingsInputClicked(elementId, 'description');
                    }}
                  />
                </ControlGroup>
              </div>
            )}

            <LinkSettingContainer
              initialConfig={linkConfig}
              allowedLinkTypes={allowedLinkTypes}
              handleChange={handleLinkChange}
              appearance={constants.common.DIALOG_BLOCK_APPEARANCE}
              captions={{
                actionTitle: i18next.t('Click action'),
                actionSubtitle: i18next.t('When user clicks on the image, make this action'),
              }}
              sourceDialog={{
                id: dialogTypes.MEDIA_SETTINGS_DIALOG,
                props: {
                  elementId,
                },
              }}
            />

            {showDeleteButton && (
              <div className="ws-modal__block">
                <ActionButton
                  label={i18next.t('Delete image')}
                  appearance="danger"
                  onClick={() => {
                    track(events.MEDIA_SETTINGS_DELETE_IMAGE_CLICKED, {
                      ...getDefaultTraits(elementId),
                      dialogId: dialogTypes.MEDIA_SETTINGS_DIALOG,
                    });
                    onDeleteImage();
                  }}
                />
              </div>
            )}
          </div>
        </Panel>
        <Panel
          height={PANEL_HEIGHT}
          align="middle"
          className="ws-media-settings-dialog__footer"
          corners="squared"
        >
          <DialogButtonGroup>
            <DialogButton onClick={handleSubmitForm}>{i18next.t('Save')}</DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={handleCancel}>{i18next.t('Cancel')}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
};

MediaSettingsDialog.propTypes = {
  elementId: PropTypes.string.isRequired,
  operations: PropTypes.array,
  getSharedData: PropTypes.func.isRequired,
  resolveSharedData: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onOpenDialog: PropTypes.func.isRequired,
  onDeleteImage: PropTypes.func,
  mediaType: PropTypes.string,
  mediaGalleryItemState: PropTypes.object,
  showDeleteButton: PropTypes.bool,
};

export default MediaSettingsDialog;
